import { isPWA } from "./pwaDetection";

// Constants
const PRODUCTION_URL = "https://wormble.com";
const DEV_MODE_KEY = "wormble-dev-share-url";

/**
 * Check if dev mode URL override is enabled
 */
export function isDevShareMode(): boolean {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(DEV_MODE_KEY) === 'true';
}

/**
 * Toggle dev mode for share URLs
 */
export function setDevShareMode(enabled: boolean): void {
  if (enabled) {
    localStorage.setItem(DEV_MODE_KEY, 'true');
  } else {
    localStorage.removeItem(DEV_MODE_KEY);
  }
}

/**
 * Get the base URL for sharing (production or local based on dev mode)
 */
function getShareBaseUrl(): string {
  // In dev mode, use local URL for testing
  if (import.meta.env.DEV && isDevShareMode()) {
    return `${window.location.origin}/play`;
  }
  return `${PRODUCTION_URL}/play`;
}

export interface ShareOptions {
  puzzleNumber: number;
  attempts?: number;
  maxAttempts?: number;
  won?: boolean;
  results?: Array<Array<"correct" | "present" | "absent">>;
  totalPlayed?: number;
  totalWon?: number;
  currentStreak?: number;
  maxStreak?: number;
  displayName?: string;
}

export interface ShareCapabilities {
  hasNativeShare: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  isPWAMode: boolean;
}

/**
 * Generate shareable text for game results
 */
export function generateShareText(options: ShareOptions): string {
  const { puzzleNumber, attempts, maxAttempts = 5, won, results } = options;
  
  // Game result format
  if (results && attempts !== undefined) {
    const title = won 
      ? `Wormble — I solved today's word in ${attempts} ${attempts === 1 ? 'guess' : 'guesses'}! Try this puzzle:` 
      : `Wormble — I couldn't crack today's puzzle. Can you?`;
    
    const emojiGrid = formatEmojiGrid(results);
    const url = generateShareUrl(puzzleNumber);
    const pwaTag = isPWA() ? "\n📱 Played on the Wormble App" : "";
    
    return `${title}\n\n${emojiGrid}\n\n${url}${pwaTag}`;
  }
  
  // Profile stats format
  if (options.totalPlayed !== undefined) {
    const { displayName, totalPlayed, totalWon, currentStreak, maxStreak } = options;
    const winRate = totalPlayed > 0 ? Math.round((totalWon! / totalPlayed) * 100) : 0;
    const name = displayName || "I";
    const url = generateShareUrl(puzzleNumber);
    
    return `🎮 ${name}'s Wormble Stats 🎮

📊 Games Played: ${totalPlayed}
✅ Games Won: ${totalWon}
🎯 Win Rate: ${winRate}%
🔥 Current Streak: ${currentStreak}
🏆 Best Streak: ${maxStreak}

Play Wormble daily at ${url}`;
  }
  
  return `Play Wormble #${puzzleNumber} at ${generateShareUrl(puzzleNumber)}`;
}

/**
 * Format emoji grid from results
 */
export function formatEmojiGrid(results: Array<Array<"correct" | "present" | "absent">>): string {
  return results.map(row => 
    row.map(status => {
      switch (status) {
        case "correct": return "🟩";
        case "present": return "🟨";
        case "absent": return "⬛";
        default: return "⬜";
      }
    }).join("")
  ).join("\n");
}

/**
 * Generate share URL with puzzle number
 */
export function generateShareUrl(puzzleNumber: number): string {
  const baseUrl = getShareBaseUrl();
  const params = new URLSearchParams();
  params.set("puzzle", puzzleNumber.toString());
  
  return `${baseUrl}?${params.toString()}`;
}

/**
 * Generate Twitter share URL
 */
export function generateTwitterUrl(text: string, url: string): string {
  const encodedText = encodeURIComponent(text);
  const encodedUrl = encodeURIComponent(url);
  return `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`;
}

/**
 * Generate Facebook share URL using sharer endpoint
 * This works without requiring a Facebook App ID
 */
export function generateFacebookUrl(url: string): string {
  return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
}

/**
 * Generate LinkedIn share URL
 */
export function generateLinkedInUrl(url: string): string {
  return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
}

/**
 * Generate WhatsApp share URL
 */
export function generateWhatsAppUrl(text: string): string {
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

/**
 * Generate SMS share URL
 */
export function generateSMSUrl(text: string): string {
  // iOS uses different format than Android
  const userAgent = navigator.userAgent.toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(userAgent);
  
  if (isIOS) {
    return `sms:&body=${encodeURIComponent(text)}`;
  }
  return `sms:?body=${encodeURIComponent(text)}`;
}

/**
 * Copy text to clipboard
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error("Failed to copy to clipboard:", err);
    return false;
  }
}

/**
 * Get platform share capabilities
 */
export function getPlatformShareCapabilities(): ShareCapabilities {
  const userAgent = navigator.userAgent.toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(userAgent);
  const isAndroid = /android/.test(userAgent);
  const hasNativeShare = typeof navigator.share !== "undefined";
  const isPWAMode = isPWA();
  
  return {
    hasNativeShare,
    isIOS,
    isAndroid,
    isPWAMode,
  };
}

/**
 * Generate AI caption for social sharing
 */
export interface CaptionOptions {
  guesses: number;
  won: boolean;
  streak: number;
  puzzleNumber: number;
  tone: "friendly" | "competitive";
}

export async function generateAICaption(options: CaptionOptions): Promise<string> {
  const { guesses, won, streak, puzzleNumber, tone } = options;
  
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-share-caption`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
      },
      body: JSON.stringify({
        guesses,
        won,
        streak,
        puzzleNumber,
        tone,
      }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Failed to generate caption");
  }

  const data = await response.json();
  return data.caption;
}

